<template>
  <div class="terrain-attachment-gallery">
    <button
      v-if="compact"
      type="button"
      class="terrain-attachment-gallery__badge"
      :title="`Ver ${attachmentIds.length} adjunto(s)`"
      @click="openLightbox(0)"
    >
      <font-awesome-icon :icon="badgeIcon" /> {{ attachmentIds.length }}
    </button>
    <ul v-else class="terrain-attachment-gallery__list">
      <li v-for="(attachmentId, index) in attachmentIds" :key="attachmentId" class="terrain-attachment-gallery__item">
        <button
          v-if="kind === 'photo'"
          type="button"
          class="terrain-attachment-gallery__thumb-btn"
          :aria-label="`Ver foto ${index + 1} de ${label}`"
          @click="openLightbox(index)"
        >
          <img :src="contentUrl(attachmentId)" :alt="`Foto ${index + 1} de ${label}`" class="terrain-attachment-gallery__thumb">
        </button>
        <button v-else type="button" class="terrain-attachment-gallery__file-link" @click="openLightbox(index)">
          <font-awesome-icon :icon="iconFor(attachmentNames[attachmentId])" /> {{ attachmentNames[attachmentId] || `Adjunto ${index + 1}` }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import { createTerrainApi } from '@/services/terrain/api.js';
import { iconForAttachmentName } from '@/services/terrain/attachmentIcons.js';
import AttachmentLightbox from './AttachmentLightbox.vue';

// AttachmentGallery muestra los Adjuntos ya subidos de una Pregunta
// file/photo y abre AttachmentLightbox (modal dinámico, this.$modal.show)
// al hacer clic — reemplaza el <a target="_blank"> que antes disparaba una
// descarga directa. Reutilizado en dos contextos: modo completo (galería de
// miniaturas/enlaces, AttachmentField.vue en modo solo-lectura) y modo
// compact (una insignia ícono+cantidad, TerrainResponsesTable.vue) — ambos
// abren el mismo lightbox, solo cambia cómo se ve el disparador.
export default {
  name: 'AttachmentGallery',
  props: {
    attachmentIds: { type: Array, default: () => [] },
    attachmentNames: { type: Object, default: () => ({}) },
    kind: { type: String, required: true, validator: (value) => ['photo', 'file'].includes(value) },
    label: { type: String, default: 'Adjuntos' },
    compact: { type: Boolean, default: false },
  },
  computed: {
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
    badgeIcon() { return this.kind === 'photo' ? 'image' : 'file-alt'; },
  },
  methods: {
    contentUrl(attachmentId) { return this.terrainApi.getAttachmentContentUrl(attachmentId); },
    iconFor(name) { return iconForAttachmentName(name); },
    openLightbox(startIndex) {
      this.$modal.show(AttachmentLightbox, {
        attachmentIds: this.attachmentIds,
        attachmentNames: this.attachmentNames,
        kind: this.kind,
        label: this.label,
        startIndex,
      }, {
        name: 'terrain-attachment-lightbox',
        width: this.kind === 'photo' ? '90%' : 480,
        maxWidth: this.kind === 'photo' ? 1100 : 480,
        // adaptive encoge el modal al tamaño natural del contenido — bien
        // para el listado de archivos (alto variable), mal para la foto
        // (el modal terminaba mucho más chico que el width pedido).
        height: this.kind === 'photo' ? '85%' : 'auto',
        adaptive: this.kind !== 'photo',
        scrollable: true,
        clickToClose: true,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-attachment-gallery__badge {
  align-items: center;
  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-sm);
  color: var(--color-navy);
  cursor: pointer;
  display: inline-flex;
  font-size: var(--terrain-font-caption);
  font-weight: 700;
  gap: var(--terrain-space-xs);
  padding: 2px var(--terrain-space-xs);
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover { background: var(--color-white); border-color: var(--color-steel); }
}

.terrain-attachment-gallery__list { display: flex; flex-wrap: wrap; gap: var(--terrain-space-sm); list-style: none; margin: 0; padding: 0; }
.terrain-attachment-gallery__item { display: block; }

.terrain-attachment-gallery__thumb-btn { background: none; border: 0; cursor: pointer; display: block; padding: 0; }
.terrain-attachment-gallery__thumb {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  height: 96px;
  object-fit: cover;
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);
  width: 96px;

  &:hover { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }
}

.terrain-attachment-gallery__file-link {
  align-items: center;
  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  color: var(--color-navy);
  cursor: pointer;
  display: inline-flex;
  font-weight: 600;
  gap: var(--terrain-space-xs);
  padding: var(--terrain-space-sm);
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover { background: var(--color-white); border-color: var(--color-steel); }
}
</style>
